// 카피바라 이동: A* 경로 따라 걷기, 서로 비켜 가기. 자세는 pose.ts.
// 프로토타입 moveTo 1702–1737, step의 goto 부분 1849–1880.
import type * as THREE from 'three';
import type { NavGrid } from '../engine/nav';
import type { Capy } from './agent';
import { climbStep } from './stacking';
import { rollStep } from './lie';

/** 걷는 속도 (m/s). */
/** 걷는 속도. 프로토타입 0.32의 1.2배 (4단계 피드백). */
export const SPEED = 0.384;

/** 목표 쪽으로 한 걸음. 땅에서는 A* 경로를 따르고, direct면 곧장 간다. 움직였으면 true. */
/** ignore: 비켜 갈 필요 없는 카피바라 (방마다 다름: 온천은 물에 잠긴 카피바라). speedK: 방별 걷는 속도 배율. */
export function moveTo(a: Capy, goal: { x: number; z: number }, dt: number, t: number, nav: NavGrid, others: readonly Capy[], direct = false, ignore?: (o: Capy) => boolean, speedK = 1): boolean {
  const dg = Math.hypot(goal.x - a.pos.x, goal.z - a.pos.z);
  if (dg <= 0.06) { a.path = null; return false; }
  let tx = goal.x, tz = goal.z, last = true;
  if (!direct) {
    if (!a.path || a.pathGX !== goal.x || a.pathGZ !== goal.z || t > a.repathAt) {
      a.path = nav.findPath(a.pos.x, a.pos.z, goal.x, goal.z);
      a.pathI = 0; a.pathGX = goal.x; a.pathGZ = goal.z; a.repathAt = 1e9;
    }
    const path = a.path;
    while (a.pathI < path.length - 1 && Math.hypot(path[a.pathI]!.x - a.pos.x, path[a.pathI]!.z - a.pos.z) < 0.35) a.pathI++;
    const w = path[a.pathI]!;
    tx = w.x; tz = w.z; last = a.pathI === path.length - 1;
    if (path.partial && last && Math.hypot(w.x - a.pos.x, w.z - a.pos.z) < 0.25) return false;
  }
  let dx = tx - a.pos.x, dz = tz - a.pos.z;
  const d = Math.hypot(dx, dz) || 1;
  dx /= d; dz /= d;
  if (!direct) {
    // 가까운 카피바라는 살짝 비켜 간다
    for (const o of others) {
      if (o === a || o.riding || o.hop || ignore?.(o)) continue;
      const ox = a.pos.x - o.pos.x, oz = a.pos.z - o.pos.z;
      if (Math.abs(ox) > 1.6 || Math.abs(oz) > 1.6) continue;
      const od = Math.hypot(ox, oz);
      if (od < 1.5 && od > 0.001) { const k = (1.5 - od) / 1.5 * 0.6; dx += ox / od * k; dz += oz / od * k; }
    }
    const n = Math.hypot(dx, dz) || 1;
    dx /= n; dz /= n;
  }
  const stp = Math.min(dg, SPEED * speedK * dt);
  const nx = a.pos.x + dx * stp, nz = a.pos.z + dz * stp;
  if (direct || last || !nav.blockedAt(nx, nz) || nav.blockedAt(a.pos.x, a.pos.z)) { a.pos.x = nx; a.pos.z = nz; }
  else if (!nav.blockedAt(nx, a.pos.z)) a.pos.x = nx;
  else if (!nav.blockedAt(a.pos.x, nz)) a.pos.z = nz;
  a.targetFacing = Math.atan2(dx, dz);
  // 몇 초 동안 가까워지지 않으면 길을 다시 찾는다
  if (a.lastDg == null || dg < a.lastDg - 0.2) { a.lastDg = dg; a.lastProg = t; }
  else if (t - a.lastProg > 4) { a.repathAt = t; a.lastProg = t; a.lastDg = dg; }
  return true;
}

/** 목표가 있으면 걷고, 서 있으면 겹친 카피바라끼리 천천히 밀어낸다. 움직였으면 true. */
export function step(a: Capy, t: number, dt: number, nav: NavGrid, others: readonly Capy[]): boolean {
  if (a.riding || a.hop || a.mode === 'riding' || a.mode === 'hopping') { a.bathAmt = 0; return false; }
  if (a.mode === 'climb') return climbStep(a, t, dt, nav, others);
  if (a.rollAnim) { rollStep(a, t, nav); return false; }
  let moving = false;
  if (a.goal) {
    moving = moveTo(a, a.goal, dt, t, nav, others);
    if (!moving) a.goal = null;
  }
  if (!moving) {
    let sx = 0, sz = 0;
    for (const o of others) {
      if (o === a || o.bathAmt > 0.5 || o.riding || o.hop) continue;
      const ox = a.pos.x - o.pos.x, oz = a.pos.z - o.pos.z, od = Math.hypot(ox, oz);
      if (od < 1.6 && od > 0.001) { const k = (1.6 - od) / 1.6; sx += ox / od * k; sz += oz / od * k; }
    }
    if (Math.hypot(sx, sz) > 0.05) {
      const nx = a.pos.x + sx * dt * 0.35, nz = a.pos.z + sz * dt * 0.35;
      if (!nav.blockedAt(nx, nz)) { a.pos.x = nx; a.pos.z = nz; }
    }
  }
  return moving;
}
